/**
 * TOOL ID duplicate matching — ONE rule for the create path and the edit path
 * (P2-5 Phase 2, B-218).
 *
 * THE RULE is the create path's, unchanged: trimmed and case-insensitive, over
 * every stored row for the site (getRecords). The P2-5 spec's ruling 3 called it
 * "exact-after-strip"; it never was — handleCreateInner compared
 * `toolId.toLowerCase()` against `supplier_tool_id.trim().toLowerCase()`. The
 * ruling's intent stands (B-218): the two paths never disagree about what a
 * duplicate is, because both call findToolIdMatches.
 *
 * KNOWN RESIDUE, NOT FIXED HERE: getRecords includes rows the server has
 * soft-deleted (ghosts, B-097) and unsent pending rows, so a match can be a
 * record that is no longer on the server (B-048, accepted). Naming the record
 * (ruling 2) makes such a hit diagnosable rather than mysterious.
 *
 * Non-blocking by contract: callers warn once per value and let a second tap
 * through. Nothing here refuses anything.
 */
import { isPendingRecord } from '../sync/pendingId';

/** The comparison key: trimmed, lower-cased. '' for anything that is not text. */
export function toolIdMatchKey(value) {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

/**
 * Stored rows whose TOOL ID matches `toolId` under the rule above.
 *
 * @param {Object[]} rows — getRecords(site_id)
 * @param {string} toolId
 * @param {{excludeRecordId?: string}} [opts] — the record being edited, which
 *   always matches itself
 * @returns {Object[]} matching rows, in the order given
 */
export function findToolIdMatches(rows, toolId, { excludeRecordId } = {}) {
  const needle = toolIdMatchKey(toolId);
  if (!needle) return [];
  return (rows || []).filter((r) => r
    && r.record_id !== excludeRecordId
    && toolIdMatchKey(r.working?.supplier_tool_id) === needle);
}

/**
 * Names a matching record for the rep (ruling 2). Its TOOL ID as stored, then
 * what tells it apart from the one being entered: description (or tool type)
 * and location, else its ToolSight id; an unsent row says so.
 */
export function describeToolIdMatch(row) {
  const w = (row && row.working) || {};
  const text = (v) => (typeof v === 'string' ? v.trim() : '');
  const parts = [text(w.supplier_tool_id)];
  const what = text(w.tool_description) || text(w.tool_type);
  const where = [text(w.site_location), text(w.detail_location)].filter(Boolean).join(' ');
  if (what) parts.push(what);
  if (where) parts.push(where);
  if (isPendingRecord(row?.record_id || '')) parts.push('not sent yet');
  else if (!what && !where && row?.tool_id) parts.push(row.tool_id);
  return parts.filter(Boolean).join(' · ');
}

/**
 * The warning, naming the first match and counting the rest.
 *
 * @param {Object[]} matches — findToolIdMatches' result, non-empty
 * @param {'create'|'save'} action — what the second tap does
 */
export function toolIdDuplicateMessage(matches, action) {
  const more = matches.length > 1 ? ` and ${matches.length - 1} more` : '';
  const tail = action === 'create'
    ? 'tap Create again to add another'
    : 'tap Save again to keep it';
  return `A tool with this ID already exists here: ${describeToolIdMatch(matches[0])}${more} — ${tail}`;
}
